// client/src/components/recursos/RecursoImages.js - VERSÃO COM SINCRONIA E REGRAS EXATAS (MAIS NOVA COMO PRINCIPAL E TODA NOVA ASSUME)
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutateRecursos } from '../../hooks/useRecursos';
import useAlert from '../../hooks/useAlert';
import { UploadCloud, Trash2, Loader2, Search as SearchIcon, XCircle, Star, Image as ImageIcon, Info, ArrowLeft } from 'lucide-react';
import GaleriaBuscaModal from '../galeria/GaleriaBuscaModal';
import PropTypes from 'prop-types';
import GerarImagemIABox from '../ai/GerarImagemIABox';

const RecursoImages = ({ recurso, isCreating, onUpdate, onClose, onBackToForm }) => {
    const { setAlert } = useAlert();
    const { 
        uploadRecursoImagem, 
        removeRecursoImagem, 
        setRecursoImagemPrincipal,
        updateRecurso,
        isPending: loading 
    } = useMutateRecursos(recurso?.account);

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    
    // ESTADOS LOCAIS
    const [imagemPrincipalLocal, setImagemPrincipalLocal] = useState(null);
    const [imagensLocais, setImagensLocais] = useState({ custom: [], galeria: [] });
    const [forceUpdateKey, setForceUpdateKey] = useState(0);

    const extractApiData = (response) => {
        if (!response) return null;
        const resData = response.data || response;
        if (resData && resData.data) return resData.data;
        return resData;
    };

    // Apenas carrega os dados locais se houver mudança real do componente pai
    useEffect(() => {
        if (recurso && recurso._id) {
            setImagemPrincipalLocal(recurso.imagemPrincipal || null);
            setImagensLocais({
                custom: recurso.imagens || [],
                galeria: recurso.imagensDaGaleria || []
            });
            setForceUpdateKey(prev => prev + 1); 
        }
    }, [recurso?._id, recurso?.updatedAt]); 

    const forceImmediateUpdate = useCallback(() => {
        setForceUpdateKey(prev => prev + 1);
    }, []);

    const safeRecurso = useMemo(() => {
        if (!recurso || !recurso._id) {
            return {
                _id: null,
                nome: 'Carregando...',
                imagens: [],
                imagensDaGaleria: [],
                imagemPrincipal: null
            };
        }
        
        return {
            ...recurso,
            imagens: imagensLocais.custom,
            imagensDaGaleria: imagensLocais.galeria,
            imagemPrincipal: imagemPrincipalLocal || recurso.imagemPrincipal || null
        };
    }, [recurso, imagensLocais, imagemPrincipalLocal, forceUpdateKey]);

    const recursoId = safeRecurso._id;

    const processarImagens = useCallback((imagensArray, tipo) => {
        if (!imagensArray || !Array.isArray(imagensArray)) return [];
        
        return imagensArray.map((img, index) => {
            if (!img) return null;
            if (typeof img === 'string') return { _id: img, tipo: tipo, principal: false, url: '' };

            const urlOriginal = img.url || img.arquivo || img.path || img.caminhoS3 || img.imagemUrl || img.src;
            if (!urlOriginal) return null;

            return {
                _id: img._id || img.id || `temp-${tipo}-${index}`,
                url: urlOriginal,
                urlOriginal: urlOriginal,
                nomeOriginal: img.nomeOriginal || img.originalname || img.nome || `Imagem ${index + 1}`,
                tipo: tipo,
                principal: false
            };
        }).filter(img => img !== null);
    }, []);

    const todasImagens = useMemo(() => {
        if (!safeRecurso || !safeRecurso._id) return [];

        const imagensCustom = processarImagens(safeRecurso.imagens, 'custom');
        const imagensGaleria = processarImagens(safeRecurso.imagensDaGaleria, 'galeria');
        const todas = [...imagensGaleria, ...imagensCustom];
        
        const principalRefId = safeRecurso.imagemPrincipal?.refId?.toString();
        if (principalRefId) {
            todas.forEach(img => {
                if (img._id?.toString() === principalRefId) {
                    img.principal = true;
                }
            });
        }
        
        return todas;
    }, [safeRecurso, processarImagens, forceUpdateKey]);

    // 🚀 UPLOAD DE IMAGEM
    const handleImageUpload = () => {
        if (!recursoId || !imageFile) return;

        const tempImage = {
            _id: `temp-upload-${Date.now()}`,
            url: imagePreview,
            urlOriginal: imagePreview,
            nomeOriginal: imageFile.name,
            tipo: 'custom',
            principal: false,
            isTemp: true
        };

        setImagensLocais(prev => ({
            ...prev,
            custom: [...prev.custom, tempImage]
        }));

        // ✅ REGRA 1: QUALQUER NOVA IMAGEM ASSUME COMO PRINCIPAL NA HORA
        setImagemPrincipalLocal({ tipo: 'custom', refId: tempImage._id });
        forceImmediateUpdate();

        const formData = new FormData();
        formData.append('imagem', imageFile);
        formData.append('referenciaId', recursoId);
        formData.append('model', 'Recurso');
        formData.append('categoria', 'imagem_recurso');

        uploadRecursoImagem.mutate({ 
            recursoId: recursoId, 
            id: recursoId,
            file: imageFile,
            imagem: imageFile,
            formData: formData,
            data: formData 
        }, {
            onSuccess: (response) => {
                setImageFile(null);
                setImagePreview(null);
                const input = document.getElementById('recurso-image-upload-component');
                if (input) input.value = '';
                
                const recursoAtualizado = extractApiData(response);
                
                if (recursoAtualizado) {
                    setImagensLocais({
                        custom: recursoAtualizado.imagens || [],
                        galeria: recursoAtualizado.imagensDaGaleria || []
                    });
                    setImagemPrincipalLocal(recursoAtualizado.imagemPrincipal || null);
                    
                    if (typeof onUpdate === 'function') onUpdate(recursoAtualizado);
                }
                
                forceImmediateUpdate();
                setAlert('Imagem adicionada com sucesso!', 'success');
            },
            onError: (error) => {
                setImagensLocais(prev => ({
                    ...prev,
                    custom: prev.custom.filter(img => !img.isTemp)
                }));
                // Se der erro, tenta voltar a principal do banco original
                setImagemPrincipalLocal(recurso?.imagemPrincipal || null);
                forceImmediateUpdate();
                setAlert(error.response?.data?.error || 'Erro ao fazer upload da imagem', 'danger');
            }
        });
    };

    // 🚀 DEFINIR IMAGEM PRINCIPAL MANUALMENTE
    const handleSetMainImage = (tipo, refId) => {
        if (!recursoId || !refId) return;

        const currentPrincipalRefId = safeRecurso.imagemPrincipal?.refId?.toString();
        if (currentPrincipalRefId === refId.toString()) {
            return setAlert('Esta imagem já é a principal.', 'info');
        }

        setImagemPrincipalLocal({ refId: refId.toString(), tipo: tipo });
        forceImmediateUpdate();

        setRecursoImagemPrincipal.mutate({ 
            recursoId: recursoId, 
            id: recursoId,
            tipo: tipo, 
            refId: refId,
            data: { tipo, refId } 
        }, {
            onSuccess: (response) => {
                const recursoAtualizado = extractApiData(response);
                
                if (recursoAtualizado && recursoAtualizado.imagemPrincipal) {
                    setImagemPrincipalLocal(recursoAtualizado.imagemPrincipal);
                    if (typeof onUpdate === 'function') onUpdate(recursoAtualizado);
                }
                setAlert('Imagem principal definida com sucesso!', 'success');
            },
            onError: (error) => {
                setImagemPrincipalLocal(recurso?.imagemPrincipal || null);
                forceImmediateUpdate(); 
                setAlert(error.response?.data?.error || 'Erro ao definir imagem principal', 'danger');
            }
        });
    };

    // 🚀 EXCLUIR IMAGEM CUSTOMIZADA
    const handleCustomImageDelete = (imagemId) => {
        if (!recursoId || !imagemId) return;

        if (imagemId.toString().startsWith('temp-')) {
            setImagensLocais(prev => ({
                ...prev,
                custom: prev.custom.filter(img => img._id !== imagemId && img.id !== imagemId)
            }));
            if (safeRecurso.imagemPrincipal?.refId?.toString() === imagemId.toString()) setImagemPrincipalLocal(null);
            setAlert('Upload cancelado.', 'info');
            forceImmediateUpdate();
            return;
        }

        if (window.confirm('Tem certeza que deseja remover esta imagem customizada?')) {
            
            const novasImagensCustom = safeRecurso.imagens.filter(img => {
                const imgId = img._id || img.id;
                return imgId && imgId.toString() !== imagemId.toString();
            });
            
            setImagensLocais(prev => ({ ...prev, custom: novasImagensCustom }));
            
            // ✅ REGRA 2: AUTO-FALLBACK PEGA A IMAGEM MAIS NOVA (A ÚLTIMA DO ARRAY)
            if (safeRecurso.imagemPrincipal?.refId?.toString() === imagemId.toString()) {
                if (novasImagensCustom.length > 0) {
                    const fallbackId = novasImagensCustom[novasImagensCustom.length - 1]._id || novasImagensCustom[novasImagensCustom.length - 1].id;
                    setImagemPrincipalLocal({ tipo: 'custom', refId: fallbackId.toString() });
                } else if (safeRecurso.imagensDaGaleria && safeRecurso.imagensDaGaleria.length > 0) {
                    const galeriaLastId = safeRecurso.imagensDaGaleria[safeRecurso.imagensDaGaleria.length - 1]._id || safeRecurso.imagensDaGaleria[safeRecurso.imagensDaGaleria.length - 1].id;
                    setImagemPrincipalLocal({ tipo: 'galeria', refId: galeriaLastId.toString() });
                } else {
                    setImagemPrincipalLocal(null);
                }
            }
            
            forceImmediateUpdate();

            removeRecursoImagem.mutate({ recursoId: recursoId, id: recursoId, imagemId: imagemId }, {
                onSuccess: (response) => {
                    const recursoAtualizado = extractApiData(response);
                    
                    if (recursoAtualizado) {
                        setImagensLocais({
                            custom: recursoAtualizado.imagens || [],
                            galeria: recursoAtualizado.imagensDaGaleria || []
                        });
                        setImagemPrincipalLocal(recursoAtualizado.imagemPrincipal || null);
                        
                        if (typeof onUpdate === 'function') onUpdate(recursoAtualizado);
                    }
                    
                    forceImmediateUpdate();
                    setAlert('Imagem removida com sucesso!', 'success');
                },
                onError: (error) => {
                    setImagensLocais({ custom: recurso?.imagens || [], galeria: recurso?.imagensDaGaleria || [] });
                    setImagemPrincipalLocal(recurso?.imagemPrincipal || null);
                    forceImmediateUpdate();
                    setAlert(error.response?.data?.error || 'Erro ao remover imagem', 'danger');
                }
            });
        }
    };

    // 🚀 DESVINCULAR IMAGEM DA GALERIA
    const handleGalleryImageRemove = (imagemId) => {
        if (!recursoId || !imagemId) return;

        if (window.confirm('Tem certeza que deseja desvincular esta imagem da galeria?')) {
            const novasImagensGaleria = safeRecurso.imagensDaGaleria.filter(img => {
                const imgId = img._id || img.id || img;
                return imgId && imgId.toString() !== imagemId.toString();
            });
            
            setImagensLocais(prev => ({ ...prev, galeria: novasImagensGaleria }));
            
            let payload = { imagensDaGaleria: novasImagensGaleria.map(img => img._id || img.id || img) };
            
            // ✅ REGRA 2: AUTO-FALLBACK PEGA A IMAGEM MAIS NOVA (A ÚLTIMA DO ARRAY)
            if (safeRecurso.imagemPrincipal?.refId?.toString() === imagemId.toString()) {
                payload.imagemPrincipal = null; // O backend decide, mas adiantamos na UI
                if (novasImagensGaleria.length > 0) {
                    const galeriaLastId = novasImagensGaleria[novasImagensGaleria.length - 1]._id || novasImagensGaleria[novasImagensGaleria.length - 1].id || novasImagensGaleria[novasImagensGaleria.length - 1];
                    setImagemPrincipalLocal({ tipo: 'galeria', refId: galeriaLastId.toString() });
                } else if (safeRecurso.imagens && safeRecurso.imagens.length > 0) {
                    const customLastId = safeRecurso.imagens[safeRecurso.imagens.length - 1]._id || safeRecurso.imagens[safeRecurso.imagens.length - 1].id || safeRecurso.imagens[safeRecurso.imagens.length - 1];
                    setImagemPrincipalLocal({ tipo: 'custom', refId: customLastId.toString() });
                } else {
                    setImagemPrincipalLocal(null);
                }
            }

            forceImmediateUpdate();

            updateRecurso.mutate({ id: recursoId, recursoData: payload, data: payload }, {
                onSuccess: (response) => {
                    const recursoAtualizado = extractApiData(response);
                    
                    if (recursoAtualizado) {
                        setImagensLocais({
                            custom: recursoAtualizado.imagens || [],
                            galeria: recursoAtualizado.imagensDaGaleria || []
                        });
                        setImagemPrincipalLocal(recursoAtualizado.imagemPrincipal || null);
                        
                        if (typeof onUpdate === 'function') onUpdate(recursoAtualizado);
                    }
                    
                    forceImmediateUpdate();
                    setAlert('Imagem desvinculada com sucesso!', 'success');
                },
                onError: (error) => {
                    setImagensLocais({ custom: recurso?.imagens || [], galeria: recurso?.imagensDaGaleria || [] });
                    setImagemPrincipalLocal(recurso?.imagemPrincipal || null);
                    forceImmediateUpdate();
                    setAlert(error.response?.data?.error || 'Erro ao desvincular imagem', 'danger');
                }
            });
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setAlert('Por favor, selecione apenas arquivos de imagem.', 'danger');
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                setAlert('A imagem deve ter no máximo 5MB.', 'danger');
                return;
            }

            setImageFile(file);
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        }
    };

    const handleGalleryImageSelect = (imagemSelecionada) => {
        if (!recursoId) return;

        const imgSelectId = imagemSelecionada._id || imagemSelecionada.id;

        const isAlreadyLinked = (safeRecurso.imagensDaGaleria || []).some(img => {
            const imgId = img._id || img.id || img;
            return imgId && imgId.toString() === imgSelectId.toString();
        });
        
        if (isAlreadyLinked) {
            return setAlert('Esta imagem já está no recurso.', 'info');
        }
        
        const novosIds = [...(safeRecurso.imagensDaGaleria || []).map(img => img._id || img.id || img), imgSelectId];

        // ✅ REGRA 1: QUALQUER NOVA IMAGEM ASSUME COMO PRINCIPAL
        const novaPrincipal = { tipo: 'galeria', refId: imgSelectId };
        setImagemPrincipalLocal(novaPrincipal);
        forceImmediateUpdate();

        updateRecurso.mutate({ 
            id: recursoId, 
            recursoData: { imagensDaGaleria: novosIds, imagemPrincipal: novaPrincipal }, 
            data: { imagensDaGaleria: novosIds, imagemPrincipal: novaPrincipal } 
        }, {
            onSuccess: (response) => {
                setShowGalleryModal(false);
                
                const recursoAtualizadoDaApi = extractApiData(response);
                
                if (recursoAtualizadoDaApi) {
                    setImagensLocais({
                        custom: recursoAtualizadoDaApi.imagens || [],
                        galeria: recursoAtualizadoDaApi.imagensDaGaleria || []
                    });
                    setImagemPrincipalLocal(recursoAtualizadoDaApi.imagemPrincipal || null);
                    
                    if (typeof onUpdate === 'function') onUpdate(recursoAtualizadoDaApi);
                }
                
                forceImmediateUpdate();
                setAlert('Imagem vinculada com sucesso!', 'success');
            },
            onError: (error) => {
                setAlert(error.response?.data?.error || 'Erro ao vincular imagem', 'danger');
            }
        });
    };

    const placeholderImage = 'https://via.placeholder.com/150?text=Sem+Imagem';
    const canEditImages = recursoId && recursoId !== 'unknown-id' && !recursoId.startsWith('temp-');

    const renderHeader = () => {
        const shortId = recursoId && canEditImages ? `${recursoId.substring(0, 8)}...` : 'Carregando...';
        
        return (
            <div className="form-header">
                <h3 className="form-title">
                    {onBackToForm && (
                        <button 
                            onClick={onBackToForm}
                            className="form-close-button mr-2"
                            title="Voltar para dados do recurso"
                        >
                            <ArrowLeft size={20} />
                        </button>
                    )}
                    Gerenciar Imagens: {safeRecurso.nome}
                    <span className="ml-2 text-sm font-normal bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        ID: {shortId}
                    </span>
                </h3>
                <button 
                    onClick={onClose}
                    title="Fechar"
                    className="form-close-button"
                >
                    <XCircle size={24} />
                </button>
            </div>
        );
    };

    if (!canEditImages || isCreating) {
        return (
            <div className="form-layout">
                {renderHeader()}
                <fieldset className="form-section">
                    <legend className="form-section-title">Imagens do Recurso</legend>
                    <div className="panel-state-wrapper panel-info">
                        <Info size={24} className="icon"/>
                        <span>
                            {isCreating 
                                ? "Primeiro, salve o recurso na aba 'Dados Básicos'. Após salvar, as opções de gerenciamento de imagens aparecerão aqui."
                                : "Recurso não identificado. Recarregue a página e tente novamente."
                            }
                        </span>
                    </div>
                </fieldset>
            </div>
        );
    }

    if (!safeRecurso._id) {
        return (
            <div className="form-layout">
                {renderHeader()}
                <fieldset className="form-section">
                    <legend className="form-section-title">Imagens do Recurso</legend>
                    <div className="panel-state-wrapper">
                        <Loader2 size={24} className="animate-spin"/>
                        <span>Carregando imagens...</span>
                    </div>
                </fieldset>
            </div>
        );
    }

    return (
        <div className="form-layout">
            {renderHeader()}

            <fieldset className="form-section" disabled={loading}>
                <legend className="form-section-title">Imagens do Recurso</legend>
                
                <div className="indented-panel">
                    <h4 className="panel-subtitle">Buscar na Galeria Global</h4>
                    <p className="form-hint mb-2">Encontre e vincule imagens da galeria global de imagens</p>
                    <button 
                        type="button" 
                        onClick={() => setShowGalleryModal(true)} 
                        className="action-button action-button-secondary mt-2"
                        disabled={loading}
                    >
                        <SearchIcon size={16} className="icon" /> Buscar na Galeria
                    </button>
                </div>

                <div className="indented-panel mt-4">
                    <h4 className="panel-subtitle">Adicionar Nova Imagem Customizada</h4>
                    <p className="form-hint mb-2">Faça upload de uma nova imagem específica para este recurso</p>
                    <div className="flex items-center gap-4 flex-wrap mt-2">
                        <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                            {imagePreview ? 
                                <img 
                                    src={imagePreview} 
                                    alt="Preview" 
                                    className="w-full h-full object-cover rounded-md"
                                    onError={(e) => { e.target.src = placeholderImage; }}
                                /> : 
                                <ImageIcon size={32} className="text-gray-400" />
                            }
                        </div>
                        <div className="form-group flex-1 min-w-[250px]">
                            <label htmlFor="recurso-image-upload-component">Escolher arquivo de imagem</label>
                            <input 
                                type="file" 
                                id="recurso-image-upload-component" 
                                onChange={handleFileChange} 
                                accept="image/*" 
                                className="form-input" 
                                disabled={loading}
                            />
                            <div className="form-hint">Formatos: JPG, PNG, GIF, WebP • Máx: 5MB</div>
                        </div>
                        <button 
                            type="button" 
                            onClick={handleImageUpload} 
                            disabled={!imageFile || loading} 
                            className="action-button action-button-primary"
                        >
                            {loading ? <Loader2 className="animate-spin icon"/> : <UploadCloud size={18} className="icon" />}
                            {loading ? 'Enviando...' : 'Enviar Imagem'}
                        </button>
                    </div>

                    <GerarImagemIABox
                        aspectRatio="3:2"
                        aspectHint="Tamanho ideal: 800×600px, paisagem. Evite fotos na vertical."
                        placeholder='Ex: "foto de sala de atendimento equipada, ambiente profissional e organizado"'
                        onUseImage={(file, previewUrl) => { setImageFile(file); setImagePreview(previewUrl); }}
                        itemContexto={{ tipo: 'recurso', nome: safeRecurso?.nome, descricao: safeRecurso?.descricao, categoria: safeRecurso?.categoria?.nome }}
                    />
                </div>

                <div className="mt-6">
                    <h4 className="panel-subtitle">
                        Galeria de Imagens do Recurso 
                        <span className="ml-2 text-sm font-normal text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                            {todasImagens.length} {todasImagens.length === 1 ? 'imagem' : 'imagens'}
                        </span>
                        {todasImagens.length > 0 && (
                            <span className="ml-2 text-sm font-normal text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                                {todasImagens.filter(img => img.principal).length} principal
                            </span>
                        )}
                    </h4>
                    
                    {todasImagens.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4">
                            {todasImagens.map((img) => {
                                const uniqueKey = img._id ? img._id.toString() : `img-${forceUpdateKey}-${Math.random()}`;
                                return (
                                <div key={uniqueKey} className="relative group border rounded-lg overflow-hidden shadow-sm aspect-square bg-white hover:shadow-md transition-shadow">
                                    <img 
                                        src={img.url} 
                                        alt={img.nomeOriginal} 
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.src = placeholderImage; }}
                                    />
                                    
                                    {img.principal && (
                                        <div className="absolute top-1 right-1 bg-amber-400 text-white p-1 rounded-full z-10 shadow-sm animate-pulse">
                                            <Star size={14} fill="white" />
                                        </div>
                                    )}
                                    
                                    <div className={`absolute top-1 left-1 text-white text-xs px-1 py-0.5 rounded z-10 shadow-sm ${
                                        img.tipo === 'galeria' ? 'bg-blue-500' : 'bg-green-500'
                                    }`}>
                                        {img.tipo === 'galeria' ? 'Galeria' : 'Custom'}
                                    </div>

                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                        <button 
                                            type="button"
                                            onClick={() => handleSetMainImage(img.tipo, img._id)}
                                            disabled={img.principal}
                                            title={img.principal ? "Já é a imagem principal" : "Definir como principal"}
                                            className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                                        >
                                            <Star size={18} fill={img.principal ? "currentColor" : "none"} />
                                        </button>
                                        
                                        {img.tipo === 'galeria' ? (
                                            <button 
                                                type="button"
                                                onClick={() => handleGalleryImageRemove(img._id)}
                                                title="Desvincular Imagem"
                                                className="p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition-colors shadow-sm"
                                            >
                                                <XCircle size={18} />
                                            </button>
                                        ) : (
                                            <button 
                                                type="button"
                                                onClick={() => handleCustomImageDelete(img._id)}
                                                title="Remover Imagem"
                                                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-sm"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>

                                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                        {img.nomeOriginal}
                                    </div>
                                </div>
                            )})}
                        </div>
                    ) : (
                        <div className="panel-state-wrapper panel-info mt-4">
                            <ImageIcon size={24} className="icon"/>
                            <span>Este recurso ainda não possui imagens. Adicione imagens usando as opções acima.</span>
                        </div>
                    )}
                </div>

                {showGalleryModal && (
                    <GaleriaBuscaModal 
                        servico={safeRecurso}
                        onClose={() => setShowGalleryModal(false)}
                        onImageSelect={handleGalleryImageSelect}
                    />
                )}

                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h5 className="font-medium text-blue-900 mb-2 flex items-center">
                        <Info size={16} className="mr-2" />
                        💡 Dicas para gerenciamento de imagens
                    </h5>
                    <ul className="text-sm text-blue-700 space-y-1">
                        <li>• <strong>Imagem Principal:</strong> Será exibida como capa do recurso</li>
                        <li>• <strong>Imagens da Galeria:</strong> Compartilhadas entre recursos, apenas vinculadas</li>
                        <li>• <strong>Imagens Custom:</strong> Específicas deste recurso, armazenadas separadamente</li>
                        <li>• <strong>Atualização:</strong> As mudanças são aplicadas instantaneamente</li>
                    </ul>
                </div>
            </fieldset>
        </div>
    );
};

RecursoImages.propTypes = {
    recurso: PropTypes.object,
    isCreating: PropTypes.bool,
    onUpdate: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    onBackToForm: PropTypes.func
};

export default RecursoImages;